'use server'

import { revalidatePath } from 'next/cache'
import { redirect } from 'next/navigation'
import { createClient } from '@/lib/supabase/server'

export async function signup(formData: FormData) {
  const supabase = await createClient()

  const email = formData.get('email') as string
  const password = formData.get('password') as string
  const locale = formData.get('locale') as string

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  })

  if (error) {
    console.error('[Signup Action] Error:', error.message)
    return { error: error.message }
  }

  console.log('[Signup Action] Success:', {
    hasUser: !!data?.user,
    hasSession: !!data?.session,
    userId: data?.user?.id
  })

  // If user is created and session exists, redirect to konto
  if (data?.session) {
    revalidatePath('/', 'layout')
    redirect(`/${locale}/admin`)
  }

  // If no session (email confirmation required), return success
  return { success: true, confirmationRequired: true }
}

export async function login(formData: FormData) {
  const supabase = await createClient()

  const email = formData.get('email') as string
  const password = formData.get('password') as string
  const locale = formData.get('locale') as string
  const returnUrl = formData.get('returnUrl') as string | null
  const redirectTo = formData.get('redirectTo') as string | null

  console.log('[Login Action] Attempting login:', {
    email,
    returnUrl,
    redirectTo,
    supabaseUrl: process.env.SUPABASE_API_URL || process.env.NEXT_PUBLIC_SUPABASE_URL,
    hasToken: !!process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  })

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  if (error) {
    console.error('[Login Action] Error:', error.message)
    return { error: error.message }
  }

  // The employee Home launcher (/home) is the universal internal entrypoint, open
  // to every internal employee (tiles self-filter by permission). External roles
  // get their own area. Replaces the old /admin landing, which required admin_panel
  // and bounced most staff to /unauthorized.
  let defaultLanding = `/${locale}/home`
  if (data?.user?.id) {
    const { data: profile } = await supabase
      .schema('public')
      .from('profiles')
      .select('role')
      .eq('id', data.user.id)
      .maybeSingle()
    const role = profile?.role as string | undefined
    if (role === 'veterinarian')   defaultLanding = `/${locale}/vet`
    else if (role === 'customer')  defaultLanding = `/${locale}`
  }
  const finalRedirect = returnUrl || redirectTo || defaultLanding

  console.log('[Login Action] Success:', {
    hasUser: !!data?.user,
    hasSession: !!data?.session,
    userId: data?.user?.id,
    redirectTo: finalRedirect
  })

  revalidatePath('/', 'layout')

  // Always return the redirect URL to the client instead of calling redirect().
  // This ensures Set-Cookie headers from signInWithPassword() are included
  // in the server action response before the browser navigates.
  return { success: true, redirectTo: finalRedirect }
}

export async function getMFAFactorsForVerify() {
  const supabase = await createClient()

  const { data, error } = await supabase.auth.mfa.listFactors()

  if (error) {
    return { error: error.message, factors: [] }
  }

  // Only return verified TOTP factors
  const verifiedFactors = (data?.totp || []).filter(f => f.status === 'verified')
  return { factors: verifiedFactors }
}

export async function verifyMFALogin(factorId: string, code: string) {
  const supabase = await createClient()

  const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })

  if (challengeError) {
    return { error: challengeError.message }
  }

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challengeData.id,
    code,
  })

  if (verifyError) {
    return { error: verifyError.message }
  }

  return { success: true }
}

export async function listMFAFactors() {
  const supabase = await createClient()

  const { data, error } = await supabase.auth.mfa.listFactors()

  if (error) {
    return { error: error.message, factors: [] }
  }

  return { factors: data?.totp || [] }
}

export async function removeMFAFactor(factorId: string) {
  const supabase = await createClient()

  const { error } = await supabase.auth.mfa.unenroll({ factorId })

  if (error) {
    return { error: error.message }
  }

  return { success: true }
}

export async function enrollMFA() {
  const supabase = await createClient()

  const { data: { user }, error: authError } = await supabase.auth.getUser()
  if (authError || !user) {
    return { error: 'Not authenticated' }
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'TOTP',
  })

  if (error) {
    return { error: error.message }
  }

  return {
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
    factorId: data.id,
  }
}

export async function verifyMFAEnrollment(factorId: string, code: string) {
  const supabase = await createClient()

  const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })

  if (challengeError) {
    return { error: challengeError.message }
  }

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challengeData.id,
    code,
  })

  if (verifyError) {
    return { error: verifyError.message }
  }

  return { success: true }
}

export async function cancelMFAEnrollment(factorId: string) {
  const supabase = await createClient()

  const { error } = await supabase.auth.mfa.unenroll({ factorId })

  if (error) {
    return { error: error.message }
  }

  return { success: true }
}
